import React, {useState} from "react";
import {Lock, LockOpen} from "@mui/icons-material";
import {Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {getNodeCategoriesInternalUrl, getPackagesInternalUrl} from "../../constants/appRoutes";
import {NODE_TYPE_MERGE} from "../../constants/nodes";
import ConfirmationDialog from "../confirmation-dialog";
import PageTitle from "../page-title";
import {catalogSelector} from "../../state/catalog/catalogSelectors";
import {nodeCodeSelector, nodeIdSelector, nodeSelector} from "../../state/node/nodeSelectors";
import {approvePackage, disapprovePackage} from "../../state/package-manager/packageManagerActions";
import {userSelector} from "../../state/user/userSelectors";
import {convertCategoryIdListToPathList} from "../../utils/catalog";
import {canApprovePackage} from "../../utils/permissions";

const PackageTitle = ({id, className, categoryPath, packageId, packageTitle, packageApproved, actions = null}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const node = useSelector(nodeSelector);
  const nodeId = useSelector(nodeIdSelector);
  const nodeCode = useSelector(nodeCodeSelector);
  const catalog = useSelector(catalogSelector);
  const user = useSelector(userSelector);

  const [open, setOpen] = useState(false);

  const handleApproval = () => {
    if (packageApproved) {
      dispatch(disapprovePackage(nodeId, packageId));
    } else {
      dispatch(approvePackage(nodeId, packageId));
    }
    setOpen(false);
  };

  return (
    <Grid container id={id} justifyContent="space-between" alignItems="flex-start" wrap="nowrap" spacing={2}>
      <Grid
        id={`${id}__title`}
        className={className}
        sx={{
          "& #page-title__breadcrumbs": {
            display: "none"
          }
        }}
      >
        <PageTitle
          title={packageTitle}
          breadcrumbItems={[
            {
              to: getNodeCategoriesInternalUrl(nodeCode),
              label: t("scenes.categories.title")
            },
            ...convertCategoryIdListToPathList(categoryPath || []).map((pathToId, idx) => ({
              to: getPackagesInternalUrl(nodeCode, categoryPath.slice(0, idx + 1)),
              label: catalog.categoryPathLabels[pathToId]
            }))
          ]}
          showTitleTooltip
          titleClassname="resource-viewer__title"
          breadcrumbsClassname="resource-viewer__breadcrumbs"
          actions={[
            node?.type !== NODE_TYPE_MERGE && canApprovePackage(user, nodeId)
              ? {
                  icon: packageApproved ? <LockOpen /> : <Lock />,
                  title: packageApproved
                    ? t("modules.packageManager.packageList.approveDataset.disapprove.label")
                    : t("modules.packageManager.packageList.approveDataset.approve.label"),
                  onClick: () => setOpen(true)
                }
              : null
          ]}
        />
        <ConfirmationDialog
          open={open}
          onClose={() => setOpen(false)}
          onConfirm={handleApproval}
          title={
            packageApproved
              ? t("modules.packageManager.packageList.disapproveDialog.title")
              : t("modules.packageManager.packageList.approveDialog.title")
          }
          content={
            packageApproved
              ? t("modules.packageManager.packageList.disapproveDialog.description")
              : t("modules.packageManager.packageList.approveDialog.description")
          }
        />
      </Grid>
      {actions && (
        <Grid
          id={`${id}__actions`}
          sx={{
            marginTop: "10px",
            flexShrink: 0
          }}
        >
          {actions}
        </Grid>
      )}
    </Grid>
  );
};

export default PackageTitle;
